import { assertNever } from "@redotech/util/type";
import { DiscountValueType } from "../discount";
import {
  type DiscountSettings,
  DiscountSettingsType,
} from "./discount-db-parser";

interface DiscountDescriptionInput {
  title?: string;
  discountSettings?: DiscountSettings;
}

/**
 * Generates a human-readable description for a discount based on its settings.
 * Used for displaying discount information to customers in emails, SMS, and UI.
 *
 * Falls back to the discount title for legacy discounts without discountSettings,
 * or "Special offer" if the title is also unavailable.
 *
 * @param discount - Any object with optional title and discountSettings
 * @returns A human-readable description string
 *
 * @example
 * // Basic percentage discount
 * formatDiscountDescription({ discountSettings: { settingsType: 'SHOPIFY_BASIC', discountValueType: 'PERCENTAGE', discountValueAmount: 15 } })
 * // Returns: "15% off"
 *
 * @example
 * // Free shipping
 * formatDiscountDescription({ discountSettings: { settingsType: 'SHOPIFY_FREE_SHIPPING' } })
 * // Returns: "Free shipping"
 *
 * @example
 * // Legacy discount without settings
 * formatDiscountDescription({ title: "Welcome discount" })
 * // Returns: "Welcome discount"
 */
export function formatDiscountDescription(
  discount: DiscountDescriptionInput,
): string {
  const settings = discount.discountSettings;
  if (!settings) {
    return discount.title || "Special offer";
  }

  switch (settings.settingsType) {
    case DiscountSettingsType.BASIC:
      if (settings.discountValueType === DiscountValueType.PERCENTAGE) {
        return `${settings.discountValueAmount}% off`;
      } else if (settings.discountValueType === DiscountValueType.AMOUNT) {
        return `$${settings.discountValueAmount} off`;
      }
      return discount.title || "Special offer";

    case DiscountSettingsType.FREE_SHIPPING:
      return "Free shipping";

    case DiscountSettingsType.BXGY: {
      const customerGets = settings.customerGets;
      if (customerGets.discountValueType === DiscountValueType.PERCENTAGE) {
        if (customerGets.discountValue === 100) {
          return `Buy X Get ${customerGets.quantity} Free`;
        }
        return `Buy X Get ${customerGets.quantity} at ${customerGets.discountValue}% off`;
      }
      return "Buy X Get Y deal";
    }

    case DiscountSettingsType.DYNAMIC_RANGE:
      return `${settings.minPercentage}% - ${settings.maxPercentage}% off`;

    default:
      return assertNever(settings);
  }
}

/**
 * Generates a description for a specific percentage discount.
 * Used when a specific percentage has been selected (e.g., from a dynamic range).
 *
 * @param percentage - The discount percentage value
 * @returns A formatted percentage description
 *
 * @example
 * formatPercentageDescription(85)
 * // Returns: "85% off"
 */
export function formatPercentageDescription(percentage: number): string {
  return `${percentage}% off`;
}

/**
 * Generates a description for a specific amount discount.
 *
 * @param amount - The discount amount in dollars
 * @returns A formatted amount description
 *
 * @example
 * formatAmountDescription(10)
 * // Returns: "$10 off"
 */
export function formatAmountDescription(amount: number): string {
  return `$${amount} off`;
}
